// PURE PARTS OF THE `floor-map` PORT (`src/app/_shared/_components/_common/floor-map/`). THE
// ANGULAR COMPONENT DRIVES D3; THE PILOT HAS NO D3, SO THE TWO D3 BEHAVIORS IT RELIES ON ARE
// REPRODUCED HERE: `d3.zoomIdentity` TRANSFORMS AND THE SMOOTH (VAN WIJK) PAN-AND-ZOOM THAT A
// `zoom.transform` TRANSITION PLAYS.

// ONE EGM OR TABLE GAME FROM `/ht/egms` / `/ht/table-games` (A SUBSET OF THE ANGULAR `Egm`)
export interface Game {
  id?: number;
  mnum?: string;
  description?: string;
  sectionName?: string;
  bank?: string;
  location?: string;
  x?: number | null;
  y?: number | null;
}

// ONE ROW OF `/ht/active-games`
export interface ActiveGame {
  tableGameId?: number;
  egmId?: number;
  location?: string;
  mnum?: string;
  highGain: boolean;
}

// `GET /mf/floor/svg`
export interface FloorImage {
  id?: number;
  width?: number;
  height?: number;
  machineRadius?: number;
  floorSvg?: string;
}

// THE ANGULAR `MapFeature`
export interface MapFeature {
  title?: string;
  color: string;
  x: number;
  y: number;
}

export interface Point {
  x: number;
  y: number;
}

// A D3 ZOOM TRANSFORM: SCREEN = WORLD * k + (x, y)
export interface Transform {
  k: number;
  x: number;
  y: number;
}

export const IDENTITY: Transform = { k: 1, x: 0, y: 0 };

// A GAME WITH BOTH COORDINATES (ANGULAR'S `e.x !== null || e.y !== null` LETS A HALF-PLACED GAME
// THROUGH, WHICH D3 THEN DRAWS AT 0)
export function placed(g: Game): g is Game & { x: number; y: number } {
  return typeof g.x === "number" && typeof g.y === "number";
}

// `floorSvg.replace("{{SVG_ID}}", id)` - THE FIRST OCCURRENCE ONLY, AS `String.replace` DOES
export function floorSvgMarkup(image: FloorImage | null | undefined, svgId: string): string {
  return image?.floorSvg?.replace("{{SVG_ID}}", svgId) ?? "";
}

// `ZoomIn(x, y)`: `zoomIdentity.translate(w / 2, h / 2).scale(4).translate(-x, -y)`
export function zoomInTransform(width: number, height: number, p: Point): Transform {
  return { k: 4, x: width / 2 - 4 * p.x, y: height / 2 - 4 * p.y };
}

// THE `transform` ATTRIBUTE D3 WRITES (`Transform.toString()`)
export function transformAttr(t: Transform): string {
  return `translate(${t.x},${t.y}) scale(${t.k})`;
}

// `d3.easeCubicInOut`, THE DEFAULT EASING OF A D3 TRANSITION
export function easeCubicInOut(t: number): number {
  return ((t *= 2) <= 1 ? t * t * t : (t -= 2) * t * t + 2) / 2;
}

// `d3.interpolateZoom` (RHO = SQRT 2) BETWEEN TWO VIEWS [CENTER X, CENTER Y, VIEW WIDTH]
type View = [number, number, number];
export function interpolateZoom(p0: View, p1: View): (t: number) => View {
  const rho = Math.SQRT2;
  const rho2 = 2;
  const rho4 = 4;
  const [ux0, uy0, w0] = p0;
  const [ux1, uy1, w1] = p1;
  const dx = ux1 - ux0;
  const dy = uy1 - uy0;
  const d2 = dx * dx + dy * dy;
  if (d2 < 1e-12) {
    const S = Math.log(w1 / w0) / rho;
    return (t) => [ux0 + t * dx, uy0 + t * dy, w0 * Math.exp(rho * t * S)];
  }
  const d1 = Math.sqrt(d2);
  const b0 = (w1 * w1 - w0 * w0 + rho4 * d2) / (2 * w0 * rho2 * d1);
  const b1 = (w1 * w1 - w0 * w0 - rho4 * d2) / (2 * w1 * rho2 * d1);
  const r0 = Math.log(Math.sqrt(b0 * b0 + 1) - b0);
  const r1 = Math.log(Math.sqrt(b1 * b1 + 1) - b1);
  const S = (r1 - r0) / rho;
  return (t) => {
    const s = t * S;
    const coshr0 = Math.cosh(r0);
    const u = (w0 / (rho2 * d1)) * (coshr0 * Math.tanh(rho * s + r0) - Math.sinh(r0));
    return [ux0 + u * dx, uy0 + u * dy, (w0 * coshr0) / Math.cosh(rho * s + r0)];
  };
}

// THE TWEEN `zoom.transform` PLAYS ON A TRANSITION: THE VIEW AROUND THE EXTENT'S CENTER `p`
// (THE VIEWBOX CENTER) MOVES FROM `a` TO `b`; THE LAST FRAME IS EXACTLY `b`
export function zoomTween(a: Transform, b: Transform, width: number, height: number): (t: number) => Transform {
  const p: [number, number] = [width / 2, height / 2];
  const w = Math.max(width, height);
  const invert = (t: Transform): [number, number] => [(p[0] - t.x) / t.k, (p[1] - t.y) / t.k];
  const i = interpolateZoom([...invert(a), w / a.k], [...invert(b), w / b.k]);
  return (t) => {
    if (t >= 1) return b;
    const l = i(t);
    const k = w / l[2];
    return { k, x: p[0] - l[0] * k, y: p[1] - l[1] * k };
  };
}

// `setFeaturesOnMap()` IN THE IN PLAY PAGE: EVERY ACTIVE GAME THAT MATCHES A PLACED FLOOR GAME
// BY LOCATION, GREEN WHEN `highGain`, RED OTHERWISE
export function buildFeatures(active: readonly ActiveGame[], games: readonly Game[]): MapFeature[] {
  const out: MapFeature[] = [];
  for (const ae of active) {
    const g = games.find((e) => e.location === ae.location);
    if (g && placed(g)) out.push({ title: g.description, color: ae.highGain ? "#3e8e41" : "#ff0000", x: g.x, y: g.y });
  }
  return out;
}

// `AddFeatures()` SELECTS `circle.egm[cx=x][cy=y]`, SO A FEATURE COLORS EVERY GAME AT ITS POINT
export const pointKey = (x: number, y: number) => `${x},${y}`;
export function featureColors(features: readonly MapFeature[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const f of features) m.set(pointKey(f.x, f.y), f.color);
  return m;
}
